import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ASSETS } from './data/assets';
import { useStudio } from './hooks/useStudio';
import { api } from './lib/api';
import { resolveMediaUrl } from './lib/dialogue';
import { hydrateDialogueMedia, uploadDialogueMedia } from './lib/dialogueMedia';
import { getRoomPolygon } from './lib/roomGeometry';
import type { DialogueCameraCue, PlacedObject, RoomConfig, SunPosition } from './types';
import { ContentViewerModal } from './components/ContentViewerModal';
import { AccountModal } from './components/AccountModal';
import { AuthModal } from './components/AuthModal';
import { DialogueHUD } from './components/DialogueHUD';
import { DialoguePlaybackBar } from './components/DialoguePlaybackBar';
import { DialogueSheetPanel } from './components/DialogueSheetPanel';
import { Header } from './components/Header';
import { InspectorPanel } from './components/InspectorPanel';
import { LaunchModal } from './components/LaunchModal';
import { LibraryPanel } from './components/LibraryPanel';
import { RoomModal } from './components/RoomModal';
import { SceneChrome } from './components/SceneChrome';
import { ShareModal } from './components/ShareModal';
import { StackModal } from './components/StackModal';
import { Toast } from './components/Toast';
import { UploadModal } from './components/UploadModal';

type ModalName = 'launch' | 'auth' | 'account' | 'room' | 'share' | 'upload' | 'stack' | 'viewer' | null;
const StudioScene = lazy(() => import('./components/StudioScene').then((module) => ({ default: module.StudioScene })));


export default function App() {
  const studio = useStudio();
  const [modal, setModal] = useState<ModalName>(() => {
    if (new URLSearchParams(window.location.search).has('login')) return 'auth';
    return localStorage.getItem('expospace.project.v1') || localStorage.getItem('baechisil.project.v1') ? null : 'launch';
  });
  const [accountBusy, setAccountBusy] = useState(false);
  const [pendingAfterAuth, setPendingAfterAuth] = useState<'share' | 'upload' | null>(null);
  const [camera, setCamera] = useState<{ view: 'perspective' | 'top' | 'front'; nonce: number; focusId?: string }>({ view: 'perspective', nonce: 0 });
  const [toast, setToast] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [lightingMode, setLightingMode] = useState<'indoor' | 'outdoor'>('indoor');
  const [sunPosition, setSunPosition] = useState<SunPosition>({ azimuth: 145, elevation: 48 });
  const [stackTarget, setStackTarget] = useState<{ id: string; topY: number } | null>(null);
  const [viewTarget, setViewTarget] = useState<PlacedObject | null>(null);
  const [dialogueMode, setDialogueMode] = useState(false);
  const [selectedDialogueLineId, setSelectedDialogueLineId] = useState<string | null>(null);
  const [dialoguePlaying, setDialoguePlaying] = useState(false);
  const [dialoguePaused, setDialoguePaused] = useState(false);
  const [playbackLineId, setPlaybackLineId] = useState<string | null>(null);
  const [playbackProgressKey, setPlaybackProgressKey] = useState(0);
  const [dialogueCameraJump, setDialogueCameraJump] = useState<{ cue: DialogueCameraCue; nonce: number; instant?: boolean } | null>(null);
  const captureCameraRef = useRef<(() => DialogueCameraCue | null) | null>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackIndexRef = useRef(0);
  const playFromIndexRef = useRef<(index: number) => void>(() => undefined);

  useEffect(() => {
    api.me().then(({ user }) => studio.setUser(user)).catch(() => undefined);
  }, [studio.setUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = studio.project.dialogue;
        const dialogue = await hydrateDialogueMedia(current);
        if (cancelled || !dialogue) return;
        const needsHydration = dialogue.avatars.some((avatar) => avatar.image.blobUrl)
          || dialogue.lines.some((line) => line.voice?.blobUrl);
        if (!needsHydration) return;
        studio.setProject((previous) => ({ ...previous, dialogue }));
      } catch {
        // Ignore hydration failures; builtin avatars still work.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const match = window.location.pathname.match(/^\/share\/([^/]+)$/);
    if (!match) return;
    setModal(null);
    api.readShare(match[1]).then(({ project }) => {
      studio.setProject(project);
      studio.setSelectedId(project.objects[0]?.id ?? null);
      setReadOnly(true);
      setToast('공유된 배치안을 읽기 전용으로 열었습니다.');
    }).catch((reason) => setToast(reason instanceof Error ? reason.message : '공유 배치안을 열지 못했습니다.'));
  }, [studio.setProject, studio.setSelectedId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  const stopPlayback = useCallback(() => {
    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setDialoguePlaying(false);
    setDialoguePaused(false);
    setPlaybackLineId(null);
  }, []);

  const pausePlayback = useCallback(() => {
    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = null;
    if (audioRef.current) audioRef.current.pause();
    setDialoguePaused(true);
  }, []);

  const resumePlayback = useCallback(() => {
    const lines = studio.dialogue.lines;
    const index = playbackIndexRef.current;
    const line = lines[index];
    if (!line) return;
    setDialoguePaused(false);
    setDialoguePlaying(true);
    setPlaybackProgressKey((value) => value + 1);

    if (audioRef.current) {
      audioRef.current.play().catch(() => undefined);
    } else {
      const voiceUrl = resolveMediaUrl(line.voice);
      if (voiceUrl) {
        const audio = new Audio(voiceUrl);
        audioRef.current = audio;
        audio.play().catch(() => undefined);
      }
    }

    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = setTimeout(() => playFromIndexRef.current(index + 1), Math.max(800, line.durationMs));
  }, [studio.dialogue.lines]);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, select, [contenteditable="true"]') || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() === 'h') {
        stopPlayback();
        setDialogueMode(false);
        studio.setTool('orbit');
        setToast('손 모드: 드래그 또는 방향키로 화면을 이동합니다.');
      }
      if (event.key.toLowerCase() === 'v') {
        stopPlayback();
        setDialogueMode(false);
        studio.setTool('select');
        setToast('선택 모드: 오브젝트를 선택하고 이동합니다.');
      }
      if (!readOnly && !dialogueMode && !modal && studio.selectedId && (event.key === 'Backspace' || event.key === 'Delete')) {
        event.preventDefault();
        studio.deleteObject(studio.selectedId);
        setToast('선택한 오브젝트를 삭제했습니다.');
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [dialogueMode, modal, readOnly, stopPlayback, studio.deleteObject, studio.selectedId, studio.setTool]);

  const selectTool = useCallback((next: 'select' | 'orbit' | 'move') => {
    stopPlayback();
    setDialogueMode(false);
    studio.setTool(next);
    if (next === 'orbit') setToast('손 모드: 드래그 또는 방향키로 화면을 이동합니다.');
    if (next === 'select') setToast('선택 모드: 오브젝트를 선택하고 이동합니다.');
  }, [stopPlayback, studio.setTool]);

  const setDialogueModeEnabled = useCallback((enabled: boolean) => {
    if (!enabled) {
      stopPlayback();
      setDialogueMode(false);
      return;
    }
    if (dialogueMode) return;
    stopPlayback();
    setDialogueMode(true);
    studio.setTool('orbit');
    studio.setSelectedId(null);
    const firstLine = studio.dialogue.lines[0]?.id ?? null;
    setSelectedDialogueLineId(firstLine);
    setToast('대화 모드: 시트를 편집하고 재생할 수 있습니다.');
  }, [dialogueMode, stopPlayback, studio]);

  const selectObject = useCallback((id: string | null) => {
    if (dialogueMode) return;
    studio.setSelectedId(id);
  }, [dialogueMode, studio.setSelectedId]);

  const focusObject = useCallback((id: string) => {
    if (dialogueMode) return;
    studio.setSelectedId(id);
    setCamera({ view: 'perspective', nonce: Date.now(), focusId: id });
  }, [dialogueMode, studio.setSelectedId]);

  const moveObject = useCallback((id: string, position: [number, number, number], wallMount?: PlacedObject['wallMount']) => {
    if (dialogueMode) return;
    studio.updateObject(id, wallMount ? { position, wallMount } : { position });
  }, [dialogueMode, studio.updateObject]);

  const rotateObject = useCallback((id: string, rotation: number) => {
    if (dialogueMode) return;
    studio.updateObject(id, { rotation });
  }, [dialogueMode, studio.updateObject]);

  const resolveAsset = useCallback((kind: string) => ASSETS.find((asset) => asset.kind === kind), []);
  const activateSelectMode = useCallback(() => {
    selectTool('select');
  }, [selectTool]);

  const requestStack = useCallback((supportId: string, topY: number) => {
    if (dialogueMode) return;
    if (!studio.canAddOnTop(supportId)) {
      setToast('오브젝트 계층은 최대 3단계까지만 배치할 수 있습니다.');
      return;
    }
    setStackTarget({ id: supportId, topY });
    setModal('stack');
  }, [dialogueMode, studio.canAddOnTop]);

  const jumpCamera = useCallback((cue: DialogueCameraCue, options?: { instant?: boolean }) => {
    setDialogueCameraJump({ cue, nonce: Date.now(), instant: options?.instant });
  }, []);
  const captureCamera = useCallback((lineId: string) => {
    const cue = captureCameraRef.current?.();
    if (!cue) {
      setToast('현재 시점을 읽지 못했습니다.');
      return;
    }
    studio.setLineCamera(lineId, cue);
    setToast('대화 라인에 시점을 저장했습니다.');
  }, [studio.setLineCamera]);

  const playFromIndex = useCallback((index: number) => {
    const lines = studio.dialogue.lines;
    if (index >= lines.length) {
      stopPlayback();
      return;
    }
    const line = lines[index];
    playbackIndexRef.current = index;
    setDialoguePaused(false);
    setDialoguePlaying(true);
    setPlaybackLineId(line.id);
    setSelectedDialogueLineId(line.id);
    setPlaybackProgressKey((value) => value + 1);
    // First cut snaps instantly from the editor view; later cuts ease with move speed.
    if (line.camera) jumpCamera(line.camera, { instant: index === 0 });

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const voiceUrl = resolveMediaUrl(line.voice);
    if (voiceUrl) {
      const audio = new Audio(voiceUrl);
      audioRef.current = audio;
      audio.play().catch(() => undefined);
    }

    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = setTimeout(() => playFromIndex(index + 1), Math.max(800, line.durationMs));
  }, [jumpCamera, stopPlayback, studio.dialogue.lines]);

  playFromIndexRef.current = playFromIndex;

  const startPlayback = useCallback(() => {
    if (!studio.dialogue.lines.length) return;
    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    playbackTimerRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setDialoguePaused(false);
    playFromIndex(0);
  }, [playFromIndex, studio.dialogue.lines.length]);

  const chooseRoom = (room: RoomConfig, name: string) => {
    studio.patchProject({ room, name });
    setModal(null);
    setCamera({ view: 'perspective', nonce: Date.now() });
  };

  const requireLogin = (returnTo: 'share' | 'upload') => {
    setPendingAfterAuth(returnTo);
    setModal('auth');
  };

  const closeAuth = () => {
    setModal(null);
    if (new URLSearchParams(window.location.search).has('login')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const applyCloudProject = useCallback((saved: typeof studio.project) => {
    const localDialogue = studio.project.dialogue;
    studio.setProject({
      ...saved,
      objects: saved.objects.map((object) => {
        const local = studio.project.objects.find((item) => item.id === object.id);
        return {
          ...object,
          ...(local?.mediaUrl ? { mediaUrl: local.mediaUrl, mediaName: local.mediaName } : {}),
          ...(local?.viewContent?.storage === 'local' ? { viewContent: local.viewContent, viewEnabled: local.viewEnabled } : {}),
        };
      }),
          dialogue: saved.dialogue
        ? {
            avatars: saved.dialogue.avatars.map((avatar) => {
              const local = localDialogue?.avatars.find((item) => item.id === avatar.id);
              if (local?.image.storage === 'local') return { ...avatar, image: local.image };
              return avatar;
            }),
            lines: saved.dialogue.lines.map((line) => {
              const local = localDialogue?.lines.find((item) => item.id === line.id);
              if (local?.voice?.storage === 'local') return { ...line, voice: local.voice };
              return line;
            }),
            hudStyle: saved.dialogue.hudStyle ?? localDialogue?.hudStyle,
            mediaCues: saved.dialogue.mediaCues ?? localDialogue?.mediaCues ?? [],
          }
        : localDialogue,
    });
  }, [studio.project, studio.setProject]);

  const onAuthSuccess = (user: Parameters<typeof studio.setUser>[0]) => {
    studio.setUser(user);
    setToast(`${user?.name}님, 로그인되었습니다.`);
    if (pendingAfterAuth) {
      setTimeout(() => setModal(pendingAfterAuth), 120);
      setPendingAfterAuth(null);
    }
  };

  const openAccountEntry = () => {
    if (studio.user) setModal('account');
    else setModal('auth');
  };

  const handleLogout = async () => {
    setAccountBusy(true);
    try {
      await api.logout();
      studio.setUser(null);
      setModal(null);
      setToast('로그아웃했습니다.');
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : '로그아웃에 실패했습니다.');
    } finally {
      setAccountBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('계정을 삭제하면 클라우드 저장 데이터도 함께 삭제됩니다. 계속할까요?')) return;
    setAccountBusy(true);
    try {
      await api.deleteAccount();
      studio.setUser(null);
      setModal(null);
      setToast('회원탈퇴가 완료되었습니다.');
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : '회원탈퇴에 실패했습니다.');
    } finally {
      setAccountBusy(false);
    }
  };

  const cloudSave = async () => {
    if (!studio.user) { setModal('auth'); return; }
    try {
      const prepared = await uploadDialogueMedia(studio.project);
      studio.setProject(prepared);
      const result = await api.saveProject(prepared);
      applyCloudProject(result.project);
      setToast('클라우드에 배치안을 저장했습니다.');
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : '클라우드 저장에 실패했습니다.');
    }
  };

  const hudLineId = dialoguePlaying ? playbackLineId : selectedDialogueLineId;

  return (
    <div className={`app-shell ${readOnly ? 'is-readonly' : ''} ${dialogueMode ? 'is-dialogue-mode' : ''}`}>
      <Header studio={studio} readOnly={readOnly} onLogin={openAccountEntry} onShare={() => setModal('share')} onCloudSave={cloudSave} />
      <main className="workspace">
        <LibraryPanel
          onAdd={(asset) => {
            if (dialogueMode) return;
            studio.addObject(asset);
          }}
          onUpload={() => {
            if (dialogueMode) return;
            setModal('upload');
          }}
        />
        <section
          className={`viewport-wrap${dialogueMode && dialoguePlaying ? ' has-playback-bar' : ''}`}
          aria-label="3D 편집 영역"
        >
          <div className="viewport-stage">
            <Suspense fallback={<div className="scene-loading" role="status"><span /><span /><span /><p>3D 공간 준비 중</p></div>}>
            <StudioScene
              project={studio.project}
              selectedId={studio.selectedId}
              tool={dialogueMode ? 'orbit' : studio.tool}
              cameraView={camera}
              onSelect={selectObject}
              onActivateSelectMode={activateSelectMode}
              onStackRequest={requestStack}
              onReadContent={(object) => { setViewTarget(object); setModal('viewer'); }}
              onRotate={rotateObject}
              onMove={moveObject}
              onAssetDrop={(asset, position) => {
                if (dialogueMode) return;
                studio.addObject(asset, position);
              }}
              resolveAsset={resolveAsset}
              readOnly={readOnly}
              lightingMode={lightingMode}
              sunPosition={sunPosition}
              dialogueMode={dialogueMode}
              dialoguePlaying={dialoguePlaying}
              selectedDialogueLineId={selectedDialogueLineId}
              playbackLineId={playbackLineId}
              dialogueCameraJump={dialogueCameraJump}
              captureCameraRef={captureCameraRef}
              onSelectDialogueLine={(lineId) => {
                setSelectedDialogueLineId(lineId);
                const line = studio.dialogue.lines.find((item) => item.id === lineId);
                if (line?.camera) jumpCamera(line.camera);
              }}
            />
            </Suspense>
            <SceneChrome
              tool={dialogueMode ? 'orbit' : studio.tool}
              project={studio.project}
              selectedId={studio.selectedId}
              readOnly={readOnly}
              dialogueMode={dialogueMode}
              cameraView={camera.view}
              onTool={selectTool}
              onDialogueMode={setDialogueModeEnabled}
              onCamera={(view) => setCamera({ view, nonce: Date.now() })}
              onFocusObject={focusObject}
              onStackObject={(id, supportId) => {
                if (dialogueMode) return;
                studio.stackExistingObject(id, supportId);
                const support = studio.project.objects.find((object) => object.id === supportId);
                setToast(`${support?.name ?? '오브젝트'} 상단에 배치했습니다.`);
              }}
              onDetachObject={(id) => {
                if (dialogueMode) return;
                studio.detachObject(id);
                setToast('상위 오브젝트에서 분리해 바닥에 배치했습니다.');
              }}
              onRoom={() => {
                if (dialogueMode) return;
                setModal('room');
              }}
              lightingMode={lightingMode}
              onLightingMode={setLightingMode}
              sunPosition={sunPosition}
              onSunPosition={setSunPosition}
            />
            {dialogueMode && (
              <DialogueHUD
                dialogue={studio.dialogue}
                lineId={hudLineId}
                visible={!!hudLineId}
                playing={dialoguePlaying}
              />
            )}
          </div>
          {dialogueMode && dialoguePlaying && (
            <DialoguePlaybackBar
              dialogue={studio.dialogue}
              objects={studio.project.objects}
              activeLineId={playbackLineId}
              paused={dialoguePaused}
              progressKey={playbackProgressKey}
              onSeek={playFromIndex}
              onPause={pausePlayback}
              onResume={resumePlayback}
              onStop={stopPlayback}
            />
          )}
        </section>
        {dialogueMode ? (
          <DialogueSheetPanel
            dialogue={studio.dialogue}
            objects={studio.project.objects}
            readOnly={readOnly}
            selectedLineId={selectedDialogueLineId}
            playing={dialoguePlaying}
            paused={dialoguePaused}
            activeLineId={playbackLineId}
            onSelectLine={setSelectedDialogueLineId}
            onUpdateAvatar={studio.updateAvatar}
            onAddGuest={studio.addGuestAvatar}
            onRemoveAvatar={studio.removeAvatar}
            onAddLine={studio.addDialogueLine}
            onUpdateLine={studio.updateDialogueLine}
            onRemoveLine={studio.removeDialogueLine}
            onMoveLine={studio.moveDialogueLine}
            onCaptureCamera={captureCamera}
            onJumpCamera={jumpCamera}
            onUpdateHudStyle={studio.updateHudStyle}
            onAssignMediaCue={(objectId, lineId, when) => {
              const replaced = (studio.dialogue.mediaCues ?? []).some((cue) => cue.objectId === objectId);
              studio.assignMediaCue(objectId, lineId, when);
              setToast(replaced ? '디스플레이 연출을 이 컷으로 옮겼습니다.' : '디스플레이 연출을 추가했습니다.');
            }}
            onUpdateMediaCue={studio.updateMediaCue}
            onRemoveMediaCue={studio.removeMediaCue}
            onPlay={startPlayback}
            onStop={stopPlayback}
          />
        ) : (
          <InspectorPanel
            object={studio.selected}
            wallCount={getRoomPolygon(studio.project.room).length - 1}
            roomHeight={studio.project.room.height}
            onUpdate={(patch) => studio.selected && studio.updateObject(studio.selected.id, patch)}
            onDelete={() => studio.selected && studio.deleteObject(studio.selected.id)}
            onDuplicate={() => studio.selected && studio.duplicateObject(studio.selected.id)}
          />
        )}
      </main>

      <LaunchModal open={modal === 'launch'} onClose={() => setModal(null)} onChoose={chooseRoom} onCustom={() => setModal('room')} />
      <AuthModal open={modal === 'auth'} onClose={closeAuth} onSuccess={onAuthSuccess} />
      {studio.user && (
        <AccountModal
          open={modal === 'account'}
          name={studio.user.name}
          email={studio.user.email}
          loading={accountBusy}
          onClose={() => setModal(null)}
          onLogout={handleLogout}
          onDeleteAccount={handleDeleteAccount}
        />
      )}
      <RoomModal open={modal === 'room'} room={studio.project.room} onClose={() => setModal(null)} onSave={(room) => { studio.patchProject({ room }); setCamera({ view: 'perspective', nonce: Date.now() }); }} />
      <ShareModal open={modal === 'share'} project={studio.project} authenticated={!!studio.user} onClose={() => setModal(null)} onRequireLogin={() => requireLogin('share')} onProjectSaved={applyCloudProject} />
      <UploadModal open={modal === 'upload'} authenticated={!!studio.user} onClose={() => setModal(null)} onRequireLogin={() => requireLogin('upload')} onUploaded={(name, url) => { studio.addCustomObject(name, url); setToast('모델을 등록하고 공간에 배치했습니다.'); }} />
      <StackModal
        open={modal === 'stack'}
        supportName={studio.project.objects.find((object) => object.id === stackTarget?.id)?.name ?? '선택한 오브젝트'}
        onClose={() => { setModal(null); setStackTarget(null); }}
        onChoose={(asset) => {
          if (!stackTarget) return;
          const support = studio.project.objects.find((object) => object.id === stackTarget.id);
          const added = studio.addObjectOnTop(asset, stackTarget.id, stackTarget.topY);
          if (!added) {
            setToast('오브젝트 계층은 최대 3단계까지만 배치할 수 있습니다.');
            return;
          }
          setModal(null);
          setStackTarget(null);
          setToast(`${support?.name ?? '오브젝트'} 위에 ${asset.name}을(를) 올렸습니다.`);
        }}
      />
      <ContentViewerModal object={modal === 'viewer' ? viewTarget : null} onClose={() => { setModal(null); setViewTarget(null); }} />
      {toast && <Toast message={toast} />}
    </div>
  );
}
